import AppLayout from "@/layouts/AppLayout";
import { useUniversityNavLinks } from "@/pages/education/universities/[universityId]/_universityNav";
import { Outlet, useParams, useLocation, useOutletContext } from "react-router-dom";
import StudentsTabLayout from "./_TabLayout";
import UniversityHeading from "../_layouts/UniversityHeading";
import StudentDetailLayout from "../_layouts/StudentDetailLayout";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import Breadcrumb from "@/components/Breadcrumb";
import type { StudentProgramSummary } from "../../types";

export default function StudentsLayout() {
  const { universityId } = useParams();
  const location = useLocation();
  const { isCompactLayout } = useIsCompactLayout();
  const navLinks = useUniversityNavLinks(universityId!);

  // Get university data from parent layout context
  const {
    universityName,
    logoUrl,
    activeStudentCount,
    alumniStudentCount,
    studentProgramSummary,
    universityId: actualUniversityId
  } = useOutletContext<{
    universityName?: string;
    logoUrl?: string | null;
    activeStudentCount?: number;
    alumniStudentCount?: number;
    studentProgramSummary?: StudentProgramSummary[];
    universityId?: string;
  }>() || {};

  // Check if this is a student detail page (e.g., /students/12345)
  const isStudentDetailPage =
    /\/students\/\d+\/?.*$/.test(location.pathname) && !location.pathname.includes("/upload-roster");

  // If this is the upload-roster page, render it directly without the university layout
  if (location.pathname.includes("/upload-roster")) {
    return <Outlet />;
  }

  // If it's a student detail page, use StudentDetailLayout with university name
  if (isStudentDetailPage) {
    return <StudentDetailLayout universityName={universityName} />;
  }

  const renderDesktopLayout = () => (
    <>
      <UniversityHeading
        universityName={universityName || ""}
        logoUrl={logoUrl}
        activeStudentCount={activeStudentCount}
        alumniStudentCount={alumniStudentCount}
        studentProgramSummary={studentProgramSummary}
      />
      <StudentsTabLayout universityId={actualUniversityId}>
        <Outlet />
      </StudentsTabLayout>
    </>
  );

  const renderMobileLayout = () => (
    <>
      <Breadcrumb
        compactOnly
        compactTitle="Students"
        backLink={{ to: "/education/universities/:universityId", params: { universityId: universityId! } }}
      />
      <UniversityHeading
        universityName={universityName || ""}
        logoUrl={logoUrl}
        activeStudentCount={activeStudentCount}
        alumniStudentCount={alumniStudentCount}
        studentProgramSummary={studentProgramSummary}
      />
      <StudentsTabLayout universityId={actualUniversityId}>
        <Outlet />
      </StudentsTabLayout>
    </>
  );

  return (
    <AppLayout navLinks={navLinks} interior bottomBar>
      {isCompactLayout ? renderMobileLayout() : renderDesktopLayout()}
    </AppLayout>
  );
}
